import React, { useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { 
  X, Plus, Trash2, Save, ShoppingCart, 
  Building, Calendar, FileText, User, CreditCard, 
  Info, DollarSign, Settings
} from 'lucide-react';
import { Modal, FormInput, FormSelect, FormTextarea, Button } from '@/components/ui';
import { fmt } from '@/lib/formatters';
import api from '@/lib/API';
import { toast } from 'react-hot-toast';
import { purchaseSchema } from '../validations/inventoryValidation';

/**
 * PurchaseForm Component
 * Refactored to use react-hook-form, zod, and reusable UI components.
 */
const PurchaseForm = ({ isOpen, onClose, user, onPurchaseSuccess, dynamicSettings, initialData = null }) => {
  const [loading, setLoading] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const isEdit = !!initialData;

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(purchaseSchema),
    defaultValues: {
      buyer: 'Generic',
      store: '',
      vendor: '',
      date: new Date().toISOString().split('T')[0],
      billNo: '',
      serviceType: user?.serviceType || "Generic",
      items: [{ item: '', qty: 1, freeQty: 0, unit: '', rate: 0, discountPercent: 0, taxPercent: 0 }],
      charges: [],
      paymentMode: 'bank',
      paidAmount: 0,
      adjustment: 0,
      remarks: ''
    }
  });

  const { fields: itemFields, append: appendItem, remove: removeItem } = useFieldArray({
    control,
    name: "items"
  });

  const { fields: chargeFields, append: appendCharge, remove: removeCharge } = useFieldArray({
    control,
    name: "charges"
  });

  const watchedItems = watch("items");
  const watchedCharges = watch("charges");
  const watchedAdjustment = watch("adjustment");

  useEffect(() => {
    if (isOpen) {
      fetchItems();
      if (initialData) {
        reset({
          buyer: initialData.buyer || 'Generic KITCHES',
          store: typeof initialData.store === 'object' ? initialData.store?._id : (initialData.store || ''),
          vendor: typeof initialData.vendor === 'object' ? initialData.vendor?._id : (initialData.vendor || ''),
          date: initialData.date ? new Date(initialData.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          billNo: initialData.billNo || '',
          serviceType: initialData.serviceType || user?.serviceType || "Generic",
          items: initialData.items?.map(item => ({
            item: typeof item.item === 'object' ? item.item?._id : (item.item || ''),
            qty: item.qty || 0,
            freeQty: item.freeQty || 0,
            unit: item.unit || '',
            rate: item.rate || 0,
            discountPercent: item.discountPercent || 0,
            taxPercent: item.taxPercent || 0,
          })) || [{ item: '', qty: 1, freeQty: 0, unit: '', rate: 0, discountPercent: 0, taxPercent: 0 }],
          charges: initialData.charges?.map(c => ({ name: c.name, amount: c.amount })) || [],
          paymentMode: initialData.payment?.paymentMode || 'bank',
          paidAmount: initialData.payment?.paidAmount || 0,
          adjustment: initialData.adjustment || 0,
          remarks: initialData.payment?.remarks || ''
        });
      } else {
        reset({
          buyer: 'Generic',
          store: '',
          vendor: '',
          date: new Date().toISOString().split('T')[0],
          billNo: '',
          serviceType: user?.serviceType || "Generic",
          items: [{ item: '', qty: 1, freeQty: 0, unit: '', rate: 0, discountPercent: 0, taxPercent: 0 }],
          charges: [],
          paymentMode: 'bank',
          paidAmount: 0,
          adjustment: 0,
          remarks: ''
        });
      }
    }
  }, [isOpen, initialData, reset, user?.serviceType]);

  const fetchItems = async () => {
    try {
      const res = await api.get('/inventory/settings?settingType=item');
      if (res.status === 'success') {
        setInventoryItems(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch inventory items:', error);
    }
  };

  const handleItemChange = (idx, itemId) => {
    const selected = inventoryItems.find(i => i._id === itemId);
    if (selected) {
      setValue(`items.${idx}.unit`, selected.units?.title || selected.units?.name || selected.unit || '');
      setValue(`items.${idx}.rate`, selected.purchaseAmount || selected.unitPrice || 0);
      setValue(`items.${idx}.taxPercent`, selected.taxPercent || 0);
    }
  };

  const totals = useMemo(() => {
    const items = watchedItems || [];
    const chargesList = watchedCharges || [];
    
    let taxable = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    items.forEach(row => {
      const qty = Number(row.qty) || 0;
      const rate = Number(row.rate) || 0;
      const discPer = Number(row.discountPercent) || 0;
      const taxPer = Number(row.taxPercent) || 0;

      const baseAmount = qty * rate;
      const discountAmt = (baseAmount * discPer) / 100;
      const rowTaxable = baseAmount - discountAmt;
      const rowTax = (rowTaxable * taxPer) / 100;

      taxable += rowTaxable;
      totalTax += rowTax;
      totalDiscount += discountAmt;
    });

    const extraCharges = chargesList.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
    const netBill = taxable + totalTax + extraCharges;
    const netPayable = netBill + (Number(watchedAdjustment) || 0);

    return { taxable, totalTax, totalDiscount, extraCharges, netBill, netPayable };
  }, [watchedItems, watchedCharges, watchedAdjustment]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const payload = {
        ...data,
        payment: {
          paymentMode: data.paymentMode,
          paidAmount: data.paidAmount,
          remarks: data.remarks
        },
        items: data.items.map(r => {
          const baseAmount = r.qty * r.rate;
          const discountAmt = (baseAmount * r.discountPercent) / 100;
          const taxable = baseAmount - discountAmt;
          const taxAmount = (taxable * r.taxPercent) / 100;
          return {
            ...r,
            taxAmount,
            amount: taxable
          };
        }),
        totalDiscount: totals.totalDiscount,
        taxableAmount: totals.taxable,
        totalTax: totals.totalTax,
        netBill: totals.netBill,
        netPayable: totals.netPayable,
      };

      let res;
      if (isEdit) {
        res = await api.patch(`/inventory/purchases/${initialData._id}`, payload);
      } else {
        res = await api.post('/inventory/purchases', payload);
      }

      if (res.status === 'success') {
        toast.success(isEdit ? "Purchase updated successfully!" : "Purchase recorded successfully!");
        onPurchaseSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(error.message || (isEdit ? "Failed to update purchase" : "Failed to record purchase"));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-[95%] h-[90vh] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-slate-100">
        
        {/* Header Section */}
        <div className="px-8 py-5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-brand-blue rounded-2xl flex items-center justify-center shadow-lg shadow-brand-blue/20">
              <ShoppingCart className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-800 tracking-tight">{isEdit ? 'EDIT PURCHASE' : 'PURCHASE ENTRY'}</h2>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{isEdit ? `Modifying Bill: ${initialData.billNo}` : 'Record New Inventory Purchase'}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X size={20} className="text-slate-400" />
          </Button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto p-8 space-y-8 no-scrollbar">
          
          {/* 1. Header Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6 p-6 rounded-2xl bg-white border border-slate-100 shadow-sm relative overflow-hidden">
             <div className="absolute top-0 left-0 w-1 h-full bg-brand-blue/30" />
             
             <FormInput 
                label="Buyer" 
                {...register("buyer")}
                icon={<Building size={14} />}
                readOnly
             />

             <FormSelect
                label="Store Location"
                required
                {...register("store")}
                error={errors.store?.message}
                icon={<Settings size={14} />}
             >
               <option value="">Select Store</option>
               {dynamicSettings?.stores?.map(s => (
                 <option key={s._id} value={s._id}>{s.title} ({s.code})</option>
               ))}
             </FormSelect>

             <FormSelect
                label="Vendor/Supplier"
                required
                {...register("vendor")}
                error={errors.vendor?.message}
                icon={<User size={14} />}
             >
               <option value="">Select Vendor</option>
               {dynamicSettings?.suppliers?.map(s => (
                 <option key={s._id} value={s._id}>{s.name || s.title}</option>
               ))}
             </FormSelect>

             <FormInput 
                label="Bill Date" 
                type="date"
                {...register("date")}
                error={errors.date?.message}
                icon={<Calendar size={14} />}
             />

             <FormInput 
                label="Bill / Invoice No" 
                required
                placeholder="INV-001"
                {...register("billNo")}
                error={errors.billNo?.message}
                icon={<FileText size={14} />}
             />
          </div>

          {/* 2. Items Table Section */}
          <div className="space-y-4">
             <div className="flex items-center justify-between px-2">
                <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                  <ShoppingCart size={16} className="text-brand-blue" />
                  Item Details & Pricing
                </h3>
             </div>

             <div className="overflow-x-auto rounded-2xl border border-slate-100 shadow-sm">
                <table className="w-full text-left border-collapse min-w-[1200px]">
                  <thead>
                    <tr className="bg-slate-50/50 border-b border-slate-100">
                      <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-12 text-center">#</th>
                      <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[250px]">Item Name / Description</th>
                      <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-24">Unit</th>
                      <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-24">Qty</th>
                      <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-24 text-emerald-600">Free Qty</th>
                      <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-28">Rate (?)</th>
                      <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-24 text-blue-600">Disc %</th>
                      <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-24 text-blue-600">Tax %</th>
                      <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-32">Amount (?)</th>
                      <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {itemFields.map((field, idx) => {
                      const row = watchedItems[idx] || {};
                      const qty = Number(row.qty) || 0;
                      const rate = Number(row.rate) || 0;
                      const discPer = Number(row.discountPercent) || 0;
                      const taxPer = Number(row.taxPercent) || 0;
                      
                      const baseAmount = qty * rate;
                      const discountAmt = (baseAmount * discPer) / 100;
                      const rowTaxable = baseAmount - discountAmt;
                      const rowTax = (rowTaxable * taxPer) / 100;
                      const rowTotal = rowTaxable + rowTax;

                      return (
                        <tr key={field.id} className="group hover:bg-slate-50/30 transition-colors">
                          <td className="px-4 py-4 text-xs font-bold text-slate-400 text-center">{idx + 1}</td>
                          <td className="px-4 py-4">
                            <Controller
                              name={`items.${idx}.item`}
                              control={control}
                              render={({ field: selectField }) => (
                                <select 
                                  {...selectField}
                                  className="w-full bg-slate-50/50 border border-slate-100 rounded-lg px-3 py-2 text-sm font-semibold outline-none focus:border-brand-blue"
                                  onChange={(e) => {
                                    selectField.onChange(e);
                                    handleItemChange(idx, e.target.value);
                                  }}
                                >
                                  <option value="">Select Item ...</option>
                                  {inventoryItems.map(item => (
                                    <option key={item._id} value={item._id}>{item.name || item.itemName} ({item.itemCode})</option>
                                  ))}
                                </select>
                              )}
                            />
                            {errors.items?.[idx]?.item && <p className="text-[10px] text-red-500 mt-1">{errors.items[idx].item.message}</p>}
                          </td>
                          <td className="px-4 py-4">
                             <input 
                                className="w-full bg-transparent border-none text-xs font-bold text-slate-500 uppercase outline-none" 
                                {...register(`items.${idx}.unit`)}
                                readOnly
                             />
                          </td>
                          <td className="px-4 py-4">
                             <input 
                                type="number" 
                                className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-bold text-slate-700 outline-none focus:border-brand-blue" 
                                {...register(`items.${idx}.qty`, { valueAsNumber: true })}
                             />
                             {errors.items?.[idx]?.qty && <p className="text-[10px] text-red-500 mt-1">{errors.items[idx].qty.message}</p>}
                          </td>
                          <td className="px-4 py-4">
                             <input 
                                type="number" 
                                className="w-full bg-emerald-50/30 border border-emerald-100 rounded-lg px-2 py-1.5 text-sm font-bold text-emerald-700 outline-none"
                                {...register(`items.${idx}.freeQty`, { valueAsNumber: true })}
                             />
                          </td>
                          <td className="px-4 py-4">
                             <input 
                                type="number" 
                                className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-bold text-slate-700 outline-none focus:border-brand-blue"
                                {...register(`items.${idx}.rate`, { valueAsNumber: true })}
                             />
                             {errors.items?.[idx]?.rate && <p className="text-[10px] text-red-500 mt-1">{errors.items[idx].rate.message}</p>}
                          </td>
                          <td className="px-4 py-4">
                             <input 
                                type="number" 
                                className="w-full bg-blue-50/30 border border-blue-100 rounded-lg px-2 py-1.5 text-sm font-bold text-blue-700 outline-none"
                                {...register(`items.${idx}.discountPercent`, { valueAsNumber: true })}
                             />
                          </td>
                          <td className="px-4 py-4">
                             <select
                                className="w-full bg-blue-50/30 border border-blue-100 rounded-lg px-2 py-1.5 text-xs font-black text-blue-700 outline-none"
                                {...register(`items.${idx}.taxPercent`, { valueAsNumber: true })}
                             >
                                <option value={0}>0%</option>
                                <option value={5}>5%</option>
                                <option value={12}>12%</option>
                                <option value={18}>18%</option>
                                <option value={28}>28%</option>
                             </select>
                          </td>
                          <td className="px-4 py-4 text-sm font-black text-slate-800">{fmt(rowTotal)}</td>
                          <td className="px-4 py-4">
                             <Button 
                                variant="ghost" 
                                size="icon-sm" 
                                onClick={() => removeItem(idx)} 
                                className="text-slate-300 hover:text-red-500"
                             >
                                <Trash2 size={16} />
                             </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
             </div>
             <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => appendItem({ item: '', qty: 1, freeQty: 0, unit: '', rate: 0, discountPercent: 0, taxPercent: 0 })}
                leftIcon={<Plus size={14} />}
                className="font-black text-[10px] tracking-widest"
             >
                ADD NEW ROW
             </Button>
          </div>

          {/* 3. Footer Section (Payment & Action) */}
          <div className="border-t border-slate-100 pt-8 pb-12">
             <div className="space-y-6">
                {/* 3a. Service Charges */}
                <div className="p-6 rounded-2xl bg-white border border-slate-100 shadow-sm relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-brand-blue/30" />
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest flex items-center gap-2 mb-4">
                    <DollarSign size={16} className="text-brand-blue" /> Service Charges & Extra Expenses
                  </h3>
                  <div className="space-y-3">
                    {chargeFields.map((field, idx) => (
                      <div key={field.id} className="flex items-center gap-3">
                        <input 
                           placeholder="Charge Name" 
                           className="flex-1 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold outline-none focus:border-brand-blue"
                           {...register(`charges.${idx}.name`)}
                        />
                        <input 
                           type="number"
                           placeholder="0.00" 
                           className="w-32 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm font-black text-slate-700 outline-none focus:border-brand-blue"
                           {...register(`charges.${idx}.amount`, { valueAsNumber: true })}
                        />
                        <Button 
                          variant="ghost" 
                          size="icon-sm" 
                          onClick={() => removeCharge(idx)} 
                          className="text-slate-300 hover:text-red-500"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                    <button 
                      type="button"
                      onClick={() => appendCharge({ name: '', amount: 0 })} 
                      className="text-[10px] font-black text-brand-blue flex items-center gap-1 mt-2 hover:underline"
                    >
                      <Plus size={12} /> ADD CHARGE
                    </button>
                  </div>
                </div>

                {/* 3b. Pay Mode Section */}
                <div className="p-6 rounded-2xl bg-white border border-slate-100 shadow-sm relative overflow-hidden space-y-4">
                  <div className="absolute top-0 left-0 w-1 h-full bg-brand-blue/30" />
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                    <CreditCard size={16} className="text-brand-blue" /> Payment Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <FormSelect
                      label="Pay Mode"
                      {...register("paymentMode")}
                      icon={<CreditCard size={14} />}
                    >
                      <option value="bank">Bank Transfer</option>
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="cheque">Cheque</option>
                    </FormSelect>
                    <FormInput
                      label="To A/C No."
                      placeholder="Acc No / ID"
                      {...register("remarks")}
                      icon={<FileText size={14} />}
                    />
                    <FormInput
                      label="Now Paying"
                      type="number"
                      {...register("paidAmount", { valueAsNumber: true })}
                      icon={<DollarSign size={14} />}
                    />
                    <FormInput
                      label="Rounding"
                      type="number"
                      placeholder="+/-"
                      {...register("adjustment", { valueAsNumber: true })}
                      icon={<Plus size={14} />}
                    />
                  </div>
                </div>

                {/* 3c. Final Amount + Submit Button Row */}
                <div className="flex flex-col lg:flex-row items-stretch gap-4">
                  <div className="bg-slate-50 px-8 py-4 rounded-2xl border border-slate-100 border-l-4 border-l-brand-blue flex flex-col md:flex-row md:items-center gap-6 flex-1">
                    <div className="flex flex-col">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] whitespace-nowrap mb-1">Final Amount To Pay</p>
                      <h3 className="text-3xl font-black text-brand-blue tracking-tighter">{fmt(totals.netPayable)}</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[10px] font-bold text-slate-500 uppercase tracking-widest md:ml-auto">
                      <span>Taxable: {fmt(totals.taxable)}</span>
                      <span>GST: {fmt(totals.totalTax)}</span>
                      <span>Discount: {fmt(totals.totalDiscount)}</span>
                      <span>Charges: {fmt(totals.extraCharges)}</span>
                    </div>
                  </div>

                  <Button 
                    type="submit"
                    loading={loading}
                    variant="primary"
                    className="px-10 h-16 lg:h-auto font-black text-xs tracking-widest shadow-xl shadow-blue-100"
                    leftIcon={!loading && <Save size={16} />}
                  >
                    {isEdit ? 'UPDATE PURCHASE' : 'RECORD PURCHASE'}
                  </Button>
                </div>
             </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export { PurchaseForm };
